export const REBALANCED_SEPERATION_DISTANCE = 1_000_000_000;
const REBALANCE_AT_DISTANCE = 100;

export const getRebalancedItemsAfterInsertion = <
    T extends { id: number; position: number },
>(
    targetItemsUnsorted: T[],
    initialInsertedItem: T,
    unboundedTargetIndex: number | undefined,
): Record<T["id"], T> => {
    const targetItems = targetItemsUnsorted.toSorted(
        (a, b) => a.position - b.position,
    );
    const targetIndex = Math.min(
        Math.max(unboundedTargetIndex ?? 0, 0),
        targetItems.length,
    );

    const prevNeighborPosition =
        targetItems[targetIndex - 1]?.position ??
        (targetItems[targetIndex]
            ? targetItems[targetIndex]?.position -
              REBALANCED_SEPERATION_DISTANCE
            : 0);
    const nextNeighborPosition =
        targetItems[targetIndex]?.position ??
        (targetItems[targetIndex - 1]
            ? targetItems[targetIndex - 1]?.position +
              REBALANCED_SEPERATION_DISTANCE
            : 0);

    const newPosition = Math.round(
        (prevNeighborPosition + nextNeighborPosition) / 2,
    );

    const separationDistance = Math.abs(
        (nextNeighborPosition - prevNeighborPosition) / 2,
    );

    const insertedItem = {
        ...initialInsertedItem,
        position: newPosition,
    };

    const needsRebalancing = separationDistance <= REBALANCE_AT_DISTANCE;

    if (!needsRebalancing) return { [insertedItem.id]: insertedItem };

    return targetItems
        .filter((item) => item.id !== insertedItem.id)
        .concat(insertedItem)
        .sort((a, b) => a.position - b.position)
        .reduce(
            (acc, item, index) => {
                acc[item.id] = {
                    ...item,
                    position: index * REBALANCED_SEPERATION_DISTANCE,
                };
                return acc;
            },
            {} as Record<number, T>,
        );
};
